import { useState } from "react"
import PropTypes from "prop-types"
import RegistrantQuestion from "./RegistrantQuestion"
import { StatusMessage } from "components/ui"
import { Button } from "@planningcenter/tapestry-react"
import { arrayToObject, isBlank, isPresent } from "utils/helpers"
import sortBy from "utils/sorter"
import { api } from "utils/api"

const apiRequest = async (originalQuestionAnswers, registration) => {
  const answers = originalQuestionAnswers
    .filter(qa => qa.request !== null)
    .map(qa => ({
      request: qa.request,
      answerPath: qa.paths.answer,
      data: {
        question_id: qa.questionId,
        answer: qa.answer,
        kind: qa.kind,
        ...((qa.kind === "dropdown" || qa.kind === "yesno" || qa.kind === "singlecheckbox") && {
          answers: qa.answers
        }),
        ...(qa.kind === "dropdown" && { choices: qa.choices }),
        ...(isPresent(qa.answerId) && { id: qa.answerId })
      }
    }))

  const newAnswers = answers.filter(a => a.request === "post").map(a => a.data)
  const existingAnswers = answers.filter(a => a.request !== "post")

  let request = []

  if (newAnswers.length > 0) {
    request.push(
      api.post(registration.paths.answer, {
        answer: { new: newAnswers, registration_id: registration.id }
      })
    )
  }

  if (existingAnswers.length > 0) {
    request = [
      ...request,
      ...existingAnswers.map(a =>
        api[a.request](a.answerPath, {
          answer: { ...a.data, registration_id: registration.id }
        })
      )
    ]
  }

  const response = await Promise.all(request)

  const created = response
    .filter(r => r.request.method === "post")
    .map(r => r.answers)
    .flat()

  const updated = response.filter(r => r.request.method === "put").map(r => r.answer)

  const deleted = response.filter(r => r.request.method === "delete").map(r => r.answer.questionId)

  const errors = response.filter(r => r.errors)

  return { created, updated, deleted, errors }
}

const initializeQuestions = questions =>
  arrayToObject(
    questions.map(q => ({
      ...q,
      request: null,
      answers: q.answers || []
    })),
    "questionId"
  )

const RegistrantQuestions = ({ registration, questions }) => {
  const [saveStatus, setSaveStatus] = useState({})
  const [isEditing, setIsEditing] = useState(false)
  const [questionAnswers, setQuestionAnswers] = useState(initializeQuestions(questions))

  const handleIsEditing = () => {
    setSaveStatus({})
    setIsEditing(!isEditing)
  }

  const handleCancel = () => {
    setQuestionAnswers(initializeQuestions(questions))
    handleIsEditing()
  }

  const handleChange = ({ target: { value, checked } }, id) => {
    const question = questionAnswers[id]

    const isDropdown = question.kind === "dropdown"
    const isSingleCheckBox = question.kind === "singlecheckbox"
    const isYesNo = question.kind === "yesno"

    let answers = []

    if (isDropdown && question.multiSelect) {
      answers = checked ? [...question.answers, value] : question.answers.filter(a => a !== value)
    }

    if ((isDropdown && !question.multiSelect) || isYesNo) {
      answers = [value]
    }

    if (isSingleCheckBox) {
      answers = [checked]
    }

    let request = null

    if (isBlank(value) && isPresent(question.answerId)) {
      request = "del"
    }

    if (isPresent(value)) {
      request = isPresent(question.answerId) ? "put" : "post"
    }

    if (isDropdown && question.multiSelect && isBlank(answers)) {
      request = "del"
    }

    setQuestionAnswers({
      ...questionAnswers,
      [id]: {
        ...question,
        answer: isDropdown && question.multiSelect ? null : value,
        answers: answers,
        request: request
      }
    })
  }

  const handleSave = async () => {
    setSaveStatus({
      isFetching: true,
      text: "Saving...",
      icon: "spinner"
    })

    const originalQuestionAnswers = Object.values(questionAnswers)

    const { created, updated, deleted, errors } = await apiRequest(
      originalQuestionAnswers,
      registration
    )

    if (errors.length > 0) {
      setSaveStatus({
        isFetching: false,
        text: errors[0].errors || "Changes not saved",
        icon: "general#x",
        type: "error"
      })

      setTimeout(() => setSaveStatus({}), 3000)
    } else {
      const newQuestionAnswers = [...originalQuestionAnswers, ...created, ...updated].map(qa => ({
        ...qa,
        answer: deleted.includes(qa.questionId) ? "" : qa.answer,
        answerId: deleted.includes(qa.questionId) ? null : qa.answerId,
        paths: {
          answer: deleted.includes(qa.questionId) ? null : qa.paths.answer
        }
      }))

      setQuestionAnswers(initializeQuestions(newQuestionAnswers))
      setSaveStatus({
        isFetching: false,
        text: "Saved!",
        icon: "general#check",
        type: "success"
      })
      handleIsEditing()
    }
  }

  return (
    <section className="pane p-2 pb-0 mb-1">
      <div className="d-f ai-c jc-sb mb-2">
        <h4 className="mb-0 fw-600">Registration questions</h4>
        {isEditing ? (
          <div>
            {isPresent(saveStatus) && <StatusMessage {...saveStatus} />}
            <Button
              title="Cancel"
              size="xs"
              variant="outline"
              marginHorizontal={1}
              disabled={saveStatus.isFetching}
              onClick={handleCancel}
            />
            <Button
              title="Save"
              size="xs"
              theme="create"
              disabled={saveStatus.isFetching}
              onClick={handleSave}
            />
          </div>
        ) : (
          <Button title="Edit" size="xs" variant="outline" onClick={handleIsEditing} />
        )}
      </div>
      <div>
        {sortBy("position", Object.values(questionAnswers)).map(question => (
          <RegistrantQuestion
            key={question.questionId}
            isEditing={isEditing}
            question={question}
            onChange={handleChange}
          />
        ))}
      </div>
    </section>
  )
}

RegistrantQuestions.propTypes = {
  questions: PropTypes.array,
  registration: PropTypes.shape({
    id: PropTypes.number,
    paths: PropTypes.shape({
      answer: PropTypes.string
    })
  })
}

export default RegistrantQuestions
